import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    InjectionToken,
    input,
    output,
    signal,
} from '@angular/core';
import {
    NgbDropdown,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
    NgbPagination,
} from '@ng-bootstrap/ng-bootstrap';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {fromControl, TranslatePipe} from '@brunata-angular/util';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {fromEvent, map, startWith} from 'rxjs';

export const PAGINATION_DEFAULTS = new InjectionToken<{
    maxSize?: number;
}>('PAGINATION_DEFAULTS');

@Component({
    selector: 'app-pagination',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgbPagination,
        ReactiveFormsModule,
        TranslatePipe,
        NgbDropdown,
        NgbDropdownToggle,
        NgbDropdownMenu,
        NgbDropdownItem,
    ],
    templateUrl: './pagination.component.html',
})
export class PaginationComponent {
    private readonly defaults = inject(PAGINATION_DEFAULTS, {optional: true});

    collectionSize = input.required<number>();
    disabled = input(false);
    maxSize = input<number>();
    page = input(1);
    pageSize = input(20);
    showTotal = input(false);
    customSelect = input(false);

    pageChange = output<number>();
    pageSizeChange = output<number>();

    readonly pageSizeOptions = [10, 20, 50, 100] as const;
    pageSizeControl = new FormControl<number>(20, {nonNullable: true});

    private readonly defaultMaxSize = computed(
        () => this.maxSize() ?? this.defaults?.maxSize ?? 10,
    );

    private readonly viewportWidth = toSignal(
        fromEvent(window, 'resize').pipe(
            startWith(null),
            map(() => window.innerWidth),
        ),
        {initialValue: window.innerWidth},
    );

    private readonly totalPages = computed(() =>
        Math.max(1, Math.ceil(this.collectionSize() / this.pageSize())),
    );

    protected responsiveMaxSize = computed(() => {
        const baseMaxSize = this.defaultMaxSize();
        const width = this.viewportWidth();
        if (width < 576) {
            return 3;
        }
        if (width < 992) {
            return 5;
        }
        return baseMaxSize;
    });

    protected currentPage = signal(1);

    constructor() {
        effect(() => this.currentPage.set(this.page()));
        effect(() => this.pageSizeControl.setValue(this.pageSize(), {emitEvent: false}));

        effect(() => {
            if (this.currentPage() <= this.totalPages()) {
                return;
            }

            this.currentPage.set(1);
            this.pageChange.emit(1);
        });

        fromControl(this.pageSizeControl)
            .pipe(takeUntilDestroyed())
            .subscribe(pageSize => this.pageSizeChange.emit(pageSize));

        effect(() => {
            if (this.disabled()) {
                this.pageSizeControl.disable();
                return;
            }
            this.pageSizeControl.enable();
        });
    }

    onPageChange(newPage: number): void {
        this.currentPage.set(newPage);
        this.pageChange.emit(newPage);
    }
}
